import React, { useState, useEffect } from "react";
import Header from "../Header";
import { useNavigate } from "react-router-dom";
import {
  getFirestore,
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  updateDoc,
  deleteDoc,
  arrayRemove,
  Timestamp
} from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword, deleteUser } from 'firebase/auth';
import { app } from "../../firebase-config";
import styles from "./Auth.module.css";

// Icon components
const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const DatabaseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3"/>
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
  </svg>
);

const AlertIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const ClipboardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const Account = () => {
  const navigate = useNavigate();
  const db = getFirestore(app);
  const auth = getAuth();

  // Delete account state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acctDeleteError, setAcctDeleteError] = useState("");
  const [acctDeleteLoading, setAcctDeleteLoading] = useState(false);

  // Delete data state
  const [confirmation, setConfirmation] = useState(false);
  const [dataDeleteSuccess, setDataDeleteSuccess] = useState(false);
  const [dataDeleteError, setDataDeleteError] = useState("");
  const [dataDeleteLoading, setDataDeleteLoading] = useState(false);

  // Study participant state
  const [isStudyParticipant, setIsStudyParticipant] = useState(false);
  const [studyStatus, setStudyStatus] = useState(null);
  const [studyLoading, setStudyLoading] = useState(true);

  useEffect(() => {
    checkStudyParticipation();
  }, []);

  const checkStudyParticipation = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setStudyLoading(false);
        return;
      }

      const participantDoc = await getDoc(doc(db, "studyParticipants", user.uid));

      if (participantDoc.exists()) {
        setIsStudyParticipant(true);
        const data = participantDoc.data();

        // Check survey completion status
        const intakeDoc = await getDoc(doc(db, "studyParticipants", user.uid, "surveys", "intake"));
        const outtakeDoc = await getDoc(doc(db, "studyParticipants", user.uid, "surveys", "outtake"));

        let outtakeReady = false;
        let daysRemaining = null;

        // Check if outtake is ready
        if (data.outtakeReadyAt) {
          const outtakeReadyDate = data.outtakeReadyAt.toDate();
          outtakeReady = new Date() >= outtakeReadyDate;
          if (!outtakeReady) {
            daysRemaining = Math.ceil((outtakeReadyDate - new Date()) / (1000 * 60 * 60 * 24));
          }
        } else if (data.studyStartedAt) {
          const studyDuration = data.studyDurationDays || 56;
          const studyStart = data.studyStartedAt.toDate();
          const outtakeDate = new Date(studyStart.getTime() + (studyDuration * 24 * 60 * 60 * 1000));
          outtakeReady = new Date() >= outtakeDate;
          if (!outtakeReady) {
            daysRemaining = Math.ceil((outtakeDate - new Date()) / (1000 * 60 * 60 * 24));
          }
        }

        setStudyStatus({
          cohort: data.cohort || 'Pilot Study',
          intakeComplete: intakeDoc.exists(),
          outtakeComplete: outtakeDoc.exists(),
          outtakeReady: outtakeReady,
          daysRemaining: daysRemaining,
          status: data.status || 'enrolled'
        });
      }

      setStudyLoading(false);
    } catch (err) {
      console.error("Error checking study participation:", err);
      setStudyLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setAcctDeleteError("");
    setAcctDeleteLoading(true);

    try {
      // Sign in to verify credentials
      await signInWithEmailAndPassword(auth, email, password);
      const userId = auth.currentUser.uid;

      // Delete subcollections
      const userDoc = doc(db, "users", userId);
      const subCollections = ['hours', 'days', 'months', 'puffs'];

      for (const subCollection of subCollections) {
        const subCollectionRef = collection(userDoc, subCollection);
        const subCollectionDocs = await getDocs(subCollectionRef);
        
        const deletePromises = subCollectionDocs.docs.map(d => deleteDoc(d.ref));
        await Promise.all(deletePromises);
      }

      // Remove from groups
      const userGroupsQuery = query(
        collection(db, "groups"), 
        where("members", "array-contains", userId)
      );
      const userGroups = await getDocs(userGroupsQuery);
      
      const groupUpdatePromises = userGroups.docs.map(group => 
        updateDoc(doc(db, "groups", group.id), {
          members: arrayRemove(userId),
        })
      );
      await Promise.all(groupUpdatePromises);

      // Delete user document
      await deleteDoc(userDoc);

      // Re-authenticate and delete auth account
      await signInWithEmailAndPassword(auth, email, password);
      await deleteUser(auth.currentUser);

      navigate("/");
    } catch (error) {
      console.log(error.message);
      
      if (error.code) {
        switch (error.code) {
          case 'auth/user-not-found':
            setAcctDeleteError("No account found with this email.");
            break;
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            setAcctDeleteError("Incorrect password. Please try again.");
            break;
          case 'auth/invalid-email':
            setAcctDeleteError("Please enter a valid email address.");
            break;
          case 'auth/too-many-requests':
            setAcctDeleteError("Too many attempts. Please try again later.");
            break;
          default:
            setAcctDeleteError("An error occurred while deleting your account.");
        }
      } else {
        setAcctDeleteError(error.message || "An error occurred while deleting your account.");
      }
    } finally {
      setAcctDeleteLoading(false);
    }
  };

  const handleDeleteData = async (e) => {
    e.preventDefault();
    setDataDeleteError("");
    setDataDeleteSuccess(false);

    if (!confirmation) {
      setDataDeleteError("Please confirm that you understand this action is irreversible.");
      return;
    }

    if (!auth.currentUser) {
      setDataDeleteError("You must be logged in to delete your data.");
      return;
    }

    setDataDeleteLoading(true);

    try {
      const userId = auth.currentUser.uid;
      const userDoc = doc(db, "users", userId);

      // Delete subcollections
      const subCollections = ['hours', 'days', 'months', 'puffs'];

      for (const subCollection of subCollections) {
        const subCollectionRef = collection(userDoc, subCollection);
        const subCollectionDocs = await getDocs(subCollectionRef);
        
        const deletePromises = subCollectionDocs.docs.map(d => deleteDoc(d.ref));
        await Promise.all(deletePromises);
      }

      // Remove from groups
      const userGroupsQuery = query(
        collection(db, "groups"), 
        where("members", "array-contains", userId)
      );
      const userGroups = await getDocs(userGroupsQuery);
      
      const groupUpdatePromises = userGroups.docs.map(group => 
        updateDoc(doc(db, "groups", group.id), {
          members: arrayRemove(userId),
        })
      );
      await Promise.all(groupUpdatePromises);

      // Reset user document fields (keep account info)
      await updateDoc(userDoc, {
        streak: 0,
        hasQuit: false,
        timestamp_last_deletion: Timestamp.now(),
        timestamp_last_update: Timestamp.now(),
        lastUpdatedMs: Date.now(),
        // Clear activity-related fields
        totalPuffs: null,
        totalDurationMs: null,
        avgPuffsPerDay: null,
      });

      setDataDeleteSuccess(true);
      setConfirmation(false);
    } catch (error) {
      console.log(error.message);
      setDataDeleteError(error.message || "An error occurred while deleting your data.");
    } finally {
      setDataDeleteLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.accountContainer}>
        <div className={styles.accountHeader}>
          <h1>Account Settings</h1>
          <p>Manage your account and data</p>
        </div>

        <div className={styles.accountGrid}>
          {/* Delete Account Card */}
          <div className={styles.accountCard}>
            <h2>
              <TrashIcon />
              Delete Account
            </h2>
            <p>
              Permanently delete your account and all associated data. 
              This action cannot be undone.
            </p>

            <form onSubmit={handleDeleteAccount} className={styles.form}>
              <div className={styles.inputGroup}>
                <div className={styles.inputWrapper}>
                  <span className={styles.inputIcon}>
                    <MailIcon />
                  </span>
                  <input
                    type="email"
                    name="email"
                    id="delete-email"
                    className={styles.input}
                    placeholder="Confirm your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <div className={styles.inputWrapper}>
                  <span className={styles.inputIcon}>
                    <LockIcon />
                  </span>
                  <input
                    type="password"
                    name="password"
                    id="delete-password"
                    className={styles.input}
                    placeholder="Confirm your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </div>
              </div>

              {acctDeleteError && (
                <div className={styles.errorMessage}>{acctDeleteError}</div>
              )}

              <button 
                type="submit" 
                className={`${styles.submitBtn} ${styles.dangerBtn}`}
                disabled={acctDeleteLoading}
              >
                {acctDeleteLoading ? (
                  <span className={styles.loading}>
                    <span className={styles.spinner}></span>
                    Deleting...
                  </span>
                ) : (
                  "Delete Account"
                )}
              </button>
            </form>
          </div>

          {/* Delete Data Card */}
          <div className={styles.accountCard}>
            <h2>
              <DatabaseIcon />
              Delete User Data
            </h2>
            <p>
              Delete all your vaping activity, group memberships, and tracking data. 
              Your account will remain active.
            </p>

            <form onSubmit={handleDeleteData} className={styles.form}>
              <label className={styles.checkboxGroup}>
                <input
                  type="checkbox"
                  name="confirm"
                  id="confirm"
                  className={styles.checkbox}
                  checked={confirmation}
                  onChange={(e) => setConfirmation(e.target.checked)}
                />
                <span className={styles.checkboxLabel}>
                  I understand that this action is irreversible and will delete all my activity data.
                </span>
              </label>

              {dataDeleteError && (
                <div className={styles.errorMessage}>{dataDeleteError}</div>
              )}

              {dataDeleteSuccess && (
                <div className={styles.successMessage}>
                  ✓ Your data has been successfully deleted.
                </div>
              )}

              <button 
                type="submit" 
                className={`${styles.submitBtn} ${styles.dangerBtn}`}
                disabled={dataDeleteLoading || !confirmation}
              >
                {dataDeleteLoading ? (
                  <span className={styles.loading}>
                    <span className={styles.spinner}></span>
                    Deleting...
                  </span>
                ) : (
                  "Delete My Data"
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Study Participation Section */}
        {!studyLoading && isStudyParticipant && (
          <div style={{ marginTop: '2rem' }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <ClipboardIcon />
              Research Study
            </h2>

            <div className={styles.accountCard} style={{ borderLeft: '3px solid #6254ba' }}>
              <div style={{ marginBottom: '1rem' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  background: 'rgba(98, 84, 186, 0.2)',
                  color: '#6254ba',
                  borderRadius: '1rem',
                  fontSize: '0.8rem',
                  fontWeight: '600'
                }}>
                  {studyStatus?.cohort || 'Pilot Study'}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {/* Intake Survey */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem',
                  background: studyStatus?.intakeComplete ? 'rgba(97, 216, 158, 0.1)' : '#1a1b1a',
                  borderRadius: '0.75rem',
                  border: studyStatus?.intakeComplete ? '1px solid rgba(97, 216, 158, 0.3)' : '1px solid #3a3b3a'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {studyStatus?.intakeComplete ? (
                      <CheckCircleIcon style={{ color: '#61d89e' }} />
                    ) : (
                      <ClipboardIcon style={{ color: '#888' }} />
                    )}
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                        Intake Survey
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#888' }}>
                        Baseline assessment (Week 0)
                      </div>
                    </div>
                  </div>

                  {studyStatus?.intakeComplete ? (
                    <span style={{
                      color: '#61d89e',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      Completed
                    </span>
                  ) : (
                    <button
                      onClick={() => navigate('/study/intake')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        background: 'linear-gradient(135deg, #61d89e 0%, #4aa87a 100%)',
                        border: 'none',
                        borderRadius: '0.5rem',
                        color: '#000',
                        fontWeight: '600',
                        fontSize: '0.85rem',
                        cursor: 'pointer'
                      }}
                    >
                      Start <ArrowRightIcon />
                    </button>
                  )}
                </div>

                {/* Outtake Survey */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem',
                  background: studyStatus?.outtakeComplete
                    ? 'rgba(97, 216, 158, 0.1)'
                    : studyStatus?.outtakeReady
                    ? '#1a1b1a'
                    : 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '0.75rem',
                  border: studyStatus?.outtakeComplete
                    ? '1px solid rgba(97, 216, 158, 0.3)'
                    : '1px solid #3a3b3a',
                  opacity: studyStatus?.intakeComplete ? 1 : 0.5
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {studyStatus?.outtakeComplete ? (
                      <CheckCircleIcon style={{ color: '#61d89e' }} />
                    ) : studyStatus?.outtakeReady ? (
                      <ClipboardIcon style={{ color: '#888' }} />
                    ) : (
                      <ClockIcon style={{ color: '#888' }} />
                    )}
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                        Outtake Survey
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#888' }}>
                        Post-intervention assessment (Week 8)
                      </div>
                    </div>
                  </div>

                  {studyStatus?.outtakeComplete ? (
                    <span style={{
                      color: '#61d89e',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      Completed
                    </span>
                  ) : studyStatus?.outtakeReady && studyStatus?.intakeComplete ? (
                    <button
                      onClick={() => navigate('/study/outtake')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        background: 'linear-gradient(135deg, #61d89e 0%, #4aa87a 100%)',
                        border: 'none',
                        borderRadius: '0.5rem',
                        color: '#000',
                        fontWeight: '600',
                        fontSize: '0.85rem',
                        cursor: 'pointer'
                      }}
                    >
                      Start <ArrowRightIcon />
                    </button>
                  ) : studyStatus?.daysRemaining ? (
                    <span style={{
                      color: '#888',
                      fontSize: '0.85rem'
                    }}>
                      Available in {studyStatus.daysRemaining} days
                    </span>
                  ) : (
                    <span style={{
                      color: '#888',
                      fontSize: '0.85rem'
                    }}>
                      Complete intake first
                    </span>
                  )}
                </div>
              </div>

              {studyStatus?.intakeComplete && studyStatus?.outtakeComplete && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  background: 'rgba(97, 216, 158, 0.1)',
                  borderRadius: '0.5rem',
                  textAlign: 'center'
                }}>
                  <span style={{ color: '#61d89e', fontWeight: '600' }}>
                    Study completed! Thank you for your participation.
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Warning Notice */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem 1.25rem',
          background: 'rgba(244, 180, 0, 0.1)',
          border: '1px solid rgba(244, 180, 0, 0.3)',
          borderRadius: '0.75rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem'
        }}>
          <AlertIcon style={{ color: '#f4b400', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong style={{ color: '#f4b400', display: 'block', marginBottom: '0.25rem' }}>
              Important Notice
            </strong>
            <p style={{ color: '#999', fontSize: '0.9rem', lineHeight: '1.5', margin: 0 }}>
              Deleting your account or data will permanently remove your progress tracking,
              streak history, and any saved preferences. If you're considering quitting the app
              rather than quitting vaping, consider just logging out instead.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;